import { createContext, use, type ReactNode } from 'react'
import { Outlet } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import { Sidebar } from '@/components/layout/Sidebar'
import { Header } from '@/components/layout/Header'

interface LayoutContextValue {
  sidebarOpen: boolean
  toggleSidebar: () => void
  scopes: string[]
}

const LayoutContext = createContext<LayoutContextValue>({
  sidebarOpen: true,
  toggleSidebar: () => {},
  scopes: [],
})

export function useLayout() {
  return use(LayoutContext)
}

interface AppLayoutProps {
  children: ReactNode
}

/**
 * Main application layout using compound component pattern.
 *
 * Provides layout context (sidebar state, scopes) to child components
 * via React Context, avoiding prop drilling.
 */
export function AppLayout({ children }: AppLayoutProps) {
  const sidebarOpen = useUiStore((s) => s.sidebarOpen)
  const toggleSidebar = useUiStore((s) => s.toggleSidebar)
  const scopes = useAuthStore((s) => s.admin?.scopes) || []

  return (
    <LayoutContext.Provider value={{ sidebarOpen, toggleSidebar, scopes }}>
      <div className="flex h-full overflow-hidden bg-surface-50 dark:bg-surface-950">
        {children}
      </div>
    </LayoutContext.Provider>
  )
}

/**
 * Default composed layout: Sidebar + Main content area with Header, Breadcrumb, and Outlet.
 */
export function DefaultLayout() {
  return (
    <AppLayout>
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />
        <main id="main-content" className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </AppLayout>
  )
}
